import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MdMarkEmailRead } from 'react-icons/md';
import { selectIata } from '_redux/slices/main-screen/user';
import * as BookingService from '_services/booking-service';
import {
    getSitaMessageType,
    cleanSitaContent,
    buildSitaMessagesQuery,
    selectFlightSitaMessages,
} from './sita-message-utils';

const titleStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    margin: '0 0 4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
};

const tableStyle = {
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    fontSize: '12px',
    margin: '8px 0',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const headerStyle = {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontWeight: '500',
    padding: '8px 12px',
    textAlign: 'left',
    borderBottom: '1px solid #e5e7eb',
    borderRight: '1px solid #e5e7eb',
    fontSize: '11px',
};

const cellStyle = {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    borderRight: '1px solid #f3f4f6',
    color: '#374151',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
};

const contentStyle = {
    margin: 0,
    fontFamily: 'monospace',
    fontSize: '11px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
};

const formatDate = (yyyyMmDd) => yyyyMmDd.split('-').reverse().join('/');

const formatReceived = (t) => {
    const d = new Date(t);
    return isNaN(d.getTime()) ? '-' : d.toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
};

// SITA messages (PAL / CAL / PSM) for the row's flight, fetched when the panel opens.
// `cache` (a Map owned by the table) keeps a re-opened panel from refetching the same flight.
const SitaMessagesPanel = ({ sitaFlight, cache }) => {
    const iata = useSelector(selectIata);
    const cacheKey = sitaFlight ? `${sitaFlight.flightCode}|${sitaFlight.flightDate}|${sitaFlight.bookingType}` : null;
    const [state, setState] = useState(() => (cacheKey && cache.has(cacheKey))
        ? { status: 'done', messages: cache.get(cacheKey) }
        : { status: 'loading', messages: [] });

    useEffect(() => {
        if (!sitaFlight || cache.has(cacheKey)) return;
        let cancelled = false;
        BookingService.getSitaMessages(buildSitaMessagesQuery({ iata, ...sitaFlight }))
            .then((res) => {
                const messages = selectFlightSitaMessages(res.rows, sitaFlight);
                cache.set(cacheKey, messages);
                if (!cancelled) setState({ status: 'done', messages });
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'error', messages: [] });
            });
        return () => { cancelled = true; };
    }, [cacheKey]);

    if (!sitaFlight) {
        return (
            <p style={{ ...titleStyle, color: '#9ca3af' }}>
                <MdMarkEmailRead size={14} /> No flight details available to look up SITA messages for this passenger.
            </p>
        );
    }

    const flightLabel = `${sitaFlight.flightCode} / ${formatDate(sitaFlight.flightDate)}`;

    if (state.status === 'loading') {
        return <p style={titleStyle}><MdMarkEmailRead size={14} color={'#2563eb'} /> Loading SITA messages for {flightLabel}…</p>;
    }
    if (state.status === 'error') {
        return <p style={{ ...titleStyle, color: '#b91c1c' }}><MdMarkEmailRead size={14} /> Could not load SITA messages for {flightLabel}. Please try again.</p>;
    }
    if (!state.messages.length) {
        return (
            <p style={{ ...titleStyle, color: '#9ca3af' }}>
                <MdMarkEmailRead size={14} /> No PAL, CAL or PSM messages have been received for {flightLabel}.
            </p>
        );
    }

    return (
        <div>
            <p style={titleStyle}>
                <MdMarkEmailRead size={14} color={'#2563eb'} /> SITA Messages — {flightLabel} ({state.messages.length})
            </p>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        {['Type', 'Received', 'Message'].map((h, i) => (
                            <th key={i} style={headerStyle}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {state.messages.map((m, i) => (
                        <tr key={m._id ?? i}>
                            <td style={{ ...cellStyle, fontWeight: 600 }}>{getSitaMessageType(m)}</td>
                            <td style={cellStyle}>{formatReceived(m.timestamp)}</td>
                            <td style={{ ...cellStyle, whiteSpace: 'normal' }}>
                                <pre style={contentStyle}>{cleanSitaContent(m.content)}</pre>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default SitaMessagesPanel;
